import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { logActivity, ACTIONS, ENTITY_TYPES } from './activityLogService';
import { updateSupplierFinancials } from './supplierService';

const PAYABLES_COLLECTION = 'tradePayables';

/**
 * Create a new trade payable
 */
export const createTradePayable = async (payableData, currentUser) => {
  try {
    const payableRef = await addDoc(collection(db, PAYABLES_COLLECTION), {
      supplierId: payableData.supplierId,
      supplierName: payableData.supplierName,
      poId: payableData.poId || null,
      poNumber: payableData.poNumber || null,
      grnId: payableData.grnId || null,
      grnNumber: payableData.grnNumber || null,
      invoiceNumber: payableData.invoiceNumber || '',
      totalAmount: payableData.totalAmount,
      paidAmount: 0,
      balanceAmount: payableData.totalAmount,
      status: 'pending',
      dueDate: payableData.dueDate,
      paymentTerms: payableData.paymentTerms || 'Net 30',
      payments: [],
      createdAt: Timestamp.now(),
      createdBy: currentUser?.uid || 'system',
      updatedAt: Timestamp.now()
    });

    await updateSupplierBalance(payableData.supplierId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.CREATED_TRADE_PAYABLE,
      entityType: ENTITY_TYPES.TRADE_PAYABLE,
      entityId: payableRef.id,
      entityName: `Payable - ${payableData.supplierName}`,
      details: { supplierName: payableData.supplierName, amount: payableData.totalAmount, grnNumber: payableData.grnNumber || '' }
    });

    return { success: true, id: payableRef.id };
  } catch (error) {
    console.error('Error creating trade payable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all trade payables
 */
export const getTradePayables = async (filters = {}) => {
  try {
    let q = collection(db, PAYABLES_COLLECTION);
    const constraints = [];

    if (filters.status) constraints.push(where('status', '==', filters.status));
    if (filters.supplierId) constraints.push(where('supplierId', '==', filters.supplierId));
    constraints.push(orderBy('createdAt', 'desc'));

    if (constraints.length > 0) q = query(q, ...constraints);

    const snapshot = await getDocs(q);
    const payables = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    return { success: true, data: payables };
  } catch (error) {
    console.error('Error getting trade payables:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get payable by ID
 */
export const getPayableById = async (payableId) => {
  try {
    const payableDoc = await getDoc(doc(db, PAYABLES_COLLECTION, payableId));
    if (!payableDoc.exists()) return { success: false, error: 'Payable not found' };
    return { success: true, data: { id: payableDoc.id, ...payableDoc.data() } };
  } catch (error) {
    console.error('Error getting payable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Record payment for a payable
 */
export const recordPayment = async (payableId, paymentData, currentUser) => {
  try {
    const payableRef = doc(db, PAYABLES_COLLECTION, payableId);
    const payableDoc = await getDoc(payableRef);
    if (!payableDoc.exists()) return { success: false, error: 'Payable not found' };

    const payableInfo = payableDoc.data();
    const currentPayments = payableInfo.payments || [];

    const newPayment = {
      id: `payment-${Date.now()}`,
      amount: parseFloat(paymentData.amount),
      date: paymentData.date || Timestamp.now(),
      paymentMethod: paymentData.paymentMethod,
      accountType: paymentData.accountType,
      reference: paymentData.reference || '',
      notes: paymentData.notes || '',
      paidBy: currentUser?.uid || 'system'
    };

    currentPayments.push(newPayment);

    const { newPaidAmount, newBalanceAmount, newStatus } = recalcPayableStatus(
      currentPayments, payableInfo.totalAmount, payableInfo.dueDate
    );

    await updateDoc(payableRef, {
      payments: currentPayments,
      paidAmount: newPaidAmount,
      balanceAmount: newBalanceAmount,
      status: newStatus,
      updatedAt: Timestamp.now()
    });

    await updateSupplierBalance(payableInfo.supplierId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.RECORDED_PAYMENT,
      entityType: ENTITY_TYPES.TRADE_PAYABLE,
      entityId: payableId,
      entityName: `Payment - ${payableInfo.supplierName}`,
      details: { supplierName: payableInfo.supplierName, amount: paymentData.amount, paymentMethod: paymentData.paymentMethod }
    });

    return { success: true, payment: newPayment };
  } catch (error) {
    console.error('Error recording payment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Recalculate paidAmount, balanceAmount, and status from a payments array.
 */
const recalcPayableStatus = (payments, totalAmount, dueDate) => {
  const newPaidAmount = payments.reduce((sum, p) => sum + p.amount, 0);
  const newBalanceAmount = totalAmount - newPaidAmount;

  let newStatus = 'pending';
  if (newBalanceAmount <= 0) {
    newStatus = 'paid';
  } else if (newPaidAmount > 0) {
    newStatus = 'partially_paid';
  }

  if (newStatus !== 'paid' && dueDate) {
    const due = dueDate.toDate ? dueDate.toDate() : new Date(dueDate);
    if (due < new Date()) newStatus = 'overdue';
  }

  return { newPaidAmount, newBalanceAmount, newStatus };
};

/**
 * Update an individual payment record within a payable.
 * Adjusts paidAmount / balanceAmount / status and issues a corrective cash ledger entry.
 * Pass { creditCashAccount, debitCashAccount } from cashBankService as the last argument.
 */
export const updatePaymentRecord = async (
  payableId,
  paymentId,
  updatedPaymentData,
  currentUser,
  { creditCashAccount, debitCashAccount }
) => {
  try {
    const payableRef = doc(db, PAYABLES_COLLECTION, payableId);
    const payableDoc = await getDoc(payableRef);
    if (!payableDoc.exists()) return { success: false, error: 'Payable not found' };

    const payableInfo = payableDoc.data();
    const payments = [...(payableInfo.payments || [])];

    const idx = payments.findIndex(p => p.id === paymentId);
    if (idx === -1) return { success: false, error: 'Payment record not found' };

    const oldPayment = payments[idx];
    const oldAmount = oldPayment.amount;
    const newAmount = parseFloat(updatedPaymentData.amount);

    if (isNaN(newAmount) || newAmount <= 0) {
      return { success: false, error: 'Invalid payment amount' };
    }

    const updatedPayment = {
      ...oldPayment,
      amount: newAmount,
      paymentMethod: updatedPaymentData.paymentMethod ?? oldPayment.paymentMethod,
      accountType: updatedPaymentData.accountType ?? oldPayment.accountType,
      reference: updatedPaymentData.reference ?? oldPayment.reference,
      notes: updatedPaymentData.notes ?? oldPayment.notes,
      editedAt: Timestamp.now(),
      editedBy: currentUser?.uid || 'system'
    };

    payments[idx] = updatedPayment;

    const { newPaidAmount, newBalanceAmount, newStatus } = recalcPayableStatus(
      payments, payableInfo.totalAmount, payableInfo.dueDate
    );

    await updateDoc(payableRef, {
      payments,
      paidAmount: newPaidAmount,
      balanceAmount: newBalanceAmount,
      status: newStatus,
      updatedAt: Timestamp.now()
    });

    // Adjust cash ledger for the difference
    const diff = newAmount - oldAmount;
    if (Math.abs(diff) > 0.001) {
      const accountType = updatedPayment.accountType;
      if (diff > 0) {
        // Extra amount paid — debit more
        await debitCashAccount(
          accountType, diff, payableId, 'trade_payable',
          `Payment Edit (additional) - ${payableInfo.supplierName}`,
          updatedPayment.paymentMethod, null, currentUser
        );
      } else {
        // Less paid — credit back the difference
        await creditCashAccount(
          accountType, Math.abs(diff), payableId, 'trade_payable',
          `Payment Edit (reversal) - ${payableInfo.supplierName}`,
          updatedPayment.paymentMethod, null, currentUser
        );
      }
    }

    await updateSupplierBalance(payableInfo.supplierId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.UPDATED_PAYMENT,
      entityType: ENTITY_TYPES.TRADE_PAYABLE,
      entityId: payableId,
      entityName: `Payment Edit - ${payableInfo.supplierName}`,
      details: { supplierName: payableInfo.supplierName, paymentId, oldAmount, newAmount, diff }
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating payment record:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete an individual payment record within a payable.
 * Reverses the cash ledger debit and recalculates totals/status.
 * Pass { creditCashAccount } from cashBankService as the last argument.
 */
export const deletePaymentRecord = async (
  payableId,
  paymentId,
  currentUser,
  { creditCashAccount }
) => {
  try {
    const payableRef = doc(db, PAYABLES_COLLECTION, payableId);
    const payableDoc = await getDoc(payableRef);
    if (!payableDoc.exists()) return { success: false, error: 'Payable not found' };

    const payableInfo = payableDoc.data();
    const payments = [...(payableInfo.payments || [])];

    const idx = payments.findIndex(p => p.id === paymentId);
    if (idx === -1) return { success: false, error: 'Payment record not found' };

    const removedPayment = payments[idx];
    payments.splice(idx, 1);

    const { newPaidAmount, newBalanceAmount, newStatus } = recalcPayableStatus(
      payments, payableInfo.totalAmount, payableInfo.dueDate
    );

    await updateDoc(payableRef, {
      payments,
      paidAmount: newPaidAmount,
      balanceAmount: newBalanceAmount,
      status: newStatus,
      updatedAt: Timestamp.now()
    });

    // Reverse the cash ledger debit
    await creditCashAccount(
      removedPayment.accountType,
      removedPayment.amount,
      payableId,
      'trade_payable',
      `Payment Deleted (reversal) - ${payableInfo.supplierName}`,
      removedPayment.paymentMethod,
      null,
      currentUser
    );

    await updateSupplierBalance(payableInfo.supplierId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.DELETED_PAYMENT,
      entityType: ENTITY_TYPES.TRADE_PAYABLE,
      entityId: payableId,
      entityName: `Payment Deleted - ${payableInfo.supplierName}`,
      details: {
        supplierName: payableInfo.supplierName,
        paymentId,
        amount: removedPayment.amount,
        accountType: removedPayment.accountType
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting payment record:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update supplier balance based on all payables
 */
const updateSupplierBalance = async (supplierId) => {
  try {
    if (!supplierId) return { success: true };

    const payablesQuery = query(
      collection(db, PAYABLES_COLLECTION),
      where('supplierId', '==', supplierId)
    );

    const snapshot = await getDocs(payablesQuery);

    let totalPurchased = 0;
    let totalPaid = 0;

    snapshot.docs.forEach(d => {
      const data = d.data();
      totalPurchased += data.totalAmount || 0;
      totalPaid += data.paidAmount || 0;
    });

    const currentBalance = totalPurchased - totalPaid;

    const supplierRef = doc(db, 'suppliers', supplierId);
    const supplierSnap = await getDoc(supplierRef);

    if (supplierSnap.exists()) {
      await updateSupplierFinancials(supplierId, { totalPurchased, totalPaid, currentBalance });
    } else {
      if (snapshot.docs.length > 0) {
        const supplierName = snapshot.docs[0].data().supplierName;
        if (supplierName) {
          const nameQuery = query(collection(db, 'suppliers'), where('name', '==', supplierName));
          const nameSnap = await getDocs(nameQuery);
          if (!nameSnap.empty) {
            await updateSupplierFinancials(nameSnap.docs[0].id, { totalPurchased, totalPaid, currentBalance });
          }
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating supplier balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get overdue payables
 */
export const getOverduePayables = async () => {
  try {
    const payablesSnapshot = await getDocs(collection(db, PAYABLES_COLLECTION));
    const now = new Date();

    const overduePayables = payablesSnapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(payable => {
        if (payable.status === 'paid') return false;
        if (!payable.dueDate) return false;
        return payable.dueDate.toDate() < now;
      });

    return { success: true, data: overduePayables };
  } catch (error) {
    console.error('Error getting overdue payables:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get supplier balance
 */
export const getSupplierBalance = async (supplierId) => {
  try {
    const payablesQuery = query(
      collection(db, PAYABLES_COLLECTION),
      where('supplierId', '==', supplierId)
    );

    const snapshot = await getDocs(payablesQuery);

    let totalAmount = 0;
    let paidAmount = 0;
    let balanceAmount = 0;
    let pendingCount = 0;

    snapshot.docs.forEach(d => {
      const data = d.data();
      totalAmount += data.totalAmount || 0;
      paidAmount += data.paidAmount || 0;
      balanceAmount += data.balanceAmount || 0;
      if (data.status !== 'paid') pendingCount++;
    });

    return {
      success: true,
      data: { totalAmount, paidAmount, balanceAmount, pendingCount, totalPayables: snapshot.size }
    };
  } catch (error) {
    console.error('Error getting supplier balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a trade payable (invoice number, due date, payment terms, notes)
 */
export const updateTradePayable = async (payableId, updateData, currentUser) => {
  try {
    const payableRef = doc(db, PAYABLES_COLLECTION, payableId);
    const payableDoc = await getDoc(payableRef);
    if (!payableDoc.exists()) return { success: false, error: 'Payable not found' };

    const existing = payableDoc.data();

    const updatedFields = {
      invoiceNumber: updateData.invoiceNumber ?? existing.invoiceNumber,
      dueDate: updateData.dueDate ?? existing.dueDate,
      paymentTerms: updateData.paymentTerms ?? existing.paymentTerms,
      notes: updateData.notes ?? existing.notes ?? '',
      updatedAt: Timestamp.now()
    };

    await updateDoc(payableRef, updatedFields);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.UPDATED_TRADE_PAYABLE,
      entityType: ENTITY_TYPES.TRADE_PAYABLE,
      entityId: payableId,
      entityName: `Payable - ${existing.supplierName}`,
      details: { supplierName: existing.supplierName, ...updatedFields }
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating trade payable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete payable linked to a specific GRN (used when GRN is deleted before any payment)
 */
export const deletePayableByGRN = async (grnId) => {
  try {
    const q = query(collection(db, PAYABLES_COLLECTION), where('grnId', '==', grnId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { success: true };

    const deletePromises = snapshot.docs.map(async (payableDoc) => {
      const data = payableDoc.data();
      if ((data.paidAmount || 0) === 0) {
        await deleteDoc(doc(db, PAYABLES_COLLECTION, payableDoc.id));
      }
    });
    await Promise.all(deletePromises);
    return { success: true };
  } catch (error) {
    console.error('Error deleting payable by GRN:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete payable (only allowed when no payments have been recorded)
 */
export const deletePayable = async (payableId, currentUser) => {
  try {
    const payableRef = doc(db, PAYABLES_COLLECTION, payableId);
    const payableDoc = await getDoc(payableRef);
    if (!payableDoc.exists()) return { success: false, error: 'Payable not found' };

    const payableData = payableDoc.data();

    if (payableData.paidAmount > 0) {
      return { success: false, error: 'Cannot delete payable with recorded payments' };
    }

    await deleteDoc(payableRef);
    await updateSupplierBalance(payableData.supplierId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.DELETED_TRADE_PAYABLE,
      entityType: ENTITY_TYPES.TRADE_PAYABLE,
      entityId: payableId,
      entityName: `Payable - ${payableData.supplierName}`,
      details: { supplierName: payableData.supplierName }
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting payable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get payables summary
 */
export const getPayablesSummary = async () => {
  try {
    const payablesSnapshot = await getDocs(collection(db, PAYABLES_COLLECTION));

    let totalOutstanding = 0;
    let totalOverdue = 0;
    let dueThisWeek = 0;
    let pendingCount = 0;

    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    payablesSnapshot.docs.forEach(d => {
      const data = d.data();
      if (data.status !== 'paid') {
        totalOutstanding += data.balanceAmount || 0;
        pendingCount++;

        if (data.dueDate) {
          const dueDate = data.dueDate.toDate();
          if (dueDate < now) {
            totalOverdue += data.balanceAmount || 0;
          } else if (dueDate <= weekFromNow) {
            dueThisWeek += data.balanceAmount || 0;
          }
        }
      }
    });

    return {
      success: true,
      data: { totalOutstanding, totalOverdue, dueThisWeek, pendingCount }
    };
  } catch (error) {
    console.error('Error getting payables summary:', error);
    return { success: false, error: error.message };
  }
};
